import { useEffect, useState } from "react";
import {
  CLIENT_TYPE_LABELS,
  PAYMENT_KIND_LABELS,
  PAYMENT_METHOD_LABELS,
  formatCad,
  type AppointmentView,
  type Client,
  type Payment,
} from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Select } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { ApiError } from "../../../core/lib/api";
import { formatDateTime } from "../../../core/lib/format";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import {
  deletePayment,
  documentUrl,
  getAppointment,
  getClient,
  issueInvoice,
  issueQuote,
  listClients,
  sendDocument,
  sendInvoice,
  updateAppointment,
} from "../lib/api";
import { amountLines } from "../lib/taxes";
import { EmailSendForm, sendOutcomeToast } from "./email-send-form";
import { InvoiceStatusBadge } from "./invoice-status-badge";
import { PaymentModal } from "./payment-modal";

interface PaymentsPanelProps {
  appointment: AppointmentView;
  /** Une autre action de la fiche est en cours : on ne cumule pas. */
  busy?: boolean;
  onChange: (view: AppointmentView) => void;
}

/**
 * Bloc « Montants » de la fiche de travail : sous-total, taxes, total, payé,
 * dû en évidence, liste des encaissements, et les actions Encaisser /
 * Facture / Devis / Envoyer par courriel. Les documents s'ouvrent dans un
 * nouvel onglet (HTML imprimable servi par l'API) après attribution du numéro
 * s'il manque.
 */
export function PaymentsPanel({ appointment: a, busy = false, onChange }: PaymentsPanelProps) {
  const push = useToastStore((s) => s.push);
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const [collecting, setCollecting] = useState(false);
  const [removing, setRemoving] = useState<Payment | null>(null);
  const [working, setWorking] = useState(false);
  const [sending, setSending] = useState(false);
  const [defaultTo, setDefaultTo] = useState<string | null>(null);
  // « Facturer à » : les concessionnaires et entreprises, choisis avant l'émission.
  const [billables, setBillables] = useState<Client[]>([]);

  const locked = busy || working;
  const settled = a.dueCents === 0 && a.totalWithTaxCents > 0;

  useEffect(() => {
    if (a.invoiceId) return;
    let cancelled = false;
    Promise.all([listClients(undefined, "concessionnaire"), listClients(undefined, "flotte")])
      .then(([dealers, fleets]) => {
        if (cancelled) return;
        setBillables(
          [...dealers, ...fleets]
            .filter((c) => c.id !== a.clientId)
            .sort((x, y) => x.name.localeCompare(y.name, "fr-CA")),
        );
      })
      .catch(() => {
        if (!cancelled) setBillables([]);
      });
    return () => {
      cancelled = true;
    };
  }, [a.invoiceId, a.clientId]);

  // Adresse proposée pour l'envoi : la partie facturée, sinon le client du véhicule.
  useEffect(() => {
    if (!sending) return;
    let cancelled = false;
    getClient(a.billedClientId ?? a.clientId)
      .then((d) => {
        if (!cancelled) setDefaultTo(d.client.email ?? null);
      })
      .catch(() => {
        if (!cancelled) setDefaultTo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [sending, a.billedClientId, a.clientId]);

  const setBilledClient = async (billedClientId: string | null) => {
    if (locked) return;
    setWorking(true);
    try {
      onChange(await updateAppointment(a.id, { billedClientId }));
    } catch {
      push("danger", "Modification impossible");
    } finally {
      setWorking(false);
    }
  };

  // Facture par courriel : émise d'abord si elle manque (comme depuis la fiche
  // avant le registre), puis envoyée par le registre avec le PDF joint.
  const send = async (to: string, message: string) => {
    setWorking(true);
    try {
      let invoiceId = a.invoiceId;
      if (!invoiceId) {
        const view = await issueInvoice(a.id);
        onChange(view);
        invoiceId = view.invoiceId;
      }
      if (!invoiceId) throw new Error("not_invoiced");
      const result = await sendInvoice(invoiceId, { to, message: message || null });
      const toast = sendOutcomeToast(result.status, result.to);
      push(toast.level, toast.title, toast.detail);
      setSending(false);
      onChange(await getAppointment(a.id));
    } catch {
      push("danger", "Envoi impossible");
    } finally {
      setWorking(false);
    }
  };

  const openDocument = async (kind: "invoice" | "quote") => {
    if (locked) return;
    // L'onglet s'ouvre AVANT l'appel réseau : un `window.open` différé est
    // bloqué par les navigateurs comme fenêtre surgissante.
    const tab = window.open("", "_blank");
    setWorking(true);
    try {
      const hasNumber = kind === "invoice" ? a.invoiceNumber : a.quoteNumber;
      const view = hasNumber
        ? a
        : kind === "invoice"
          ? await issueInvoice(a.id)
          : await issueQuote(a.id);
      if (view !== a) onChange(view);
      const url = documentUrl(a.id, kind);
      if (tab) tab.location.href = url;
      else window.open(url, "_blank");
      if (!hasNumber) {
        push(
          "success",
          kind === "invoice" ? "Facture émise" : "Devis émis",
          kind === "invoice" ? (view.invoiceNumber ?? undefined) : (view.quoteNumber ?? undefined),
        );
      }
    } catch {
      tab?.close();
      push(
        "danger",
        kind === "invoice" ? "Facturation impossible" : "Émission du devis impossible",
      );
    } finally {
      setWorking(false);
    }
  };

  // « Envoyer par courriel » : la facture (émise au besoin) avec adresse et mot
  // d'accompagnement modifiables ; ou le devis s'il n'y a pas de facture —
  // modèle « Devis », PDF joint, message visible dans Messages.
  const emailDocument = async (kind: "invoice" | "quote") => {
    if (locked) return;
    if (kind === "invoice") {
      setSending(true);
      return;
    }
    if (!window.confirm("Envoyer le devis par courriel au client ?")) return;
    setWorking(true);
    try {
      const { message, appointment } = await sendDocument(a.id, "quote");
      if (appointment !== a) onChange(appointment);
      const toast = sendOutcomeToast(message.status, message.toAddress);
      push(toast.level, toast.title, toast.detail);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      push(
        "danger",
        "Devis non envoyé",
        code === "client_email_missing"
          ? "Le client n'a pas d'adresse courriel dans sa fiche."
          : undefined,
      );
    } finally {
      setWorking(false);
    }
  };

  const remove = async () => {
    if (!removing || locked) return;
    setWorking(true);
    try {
      await deletePayment(removing.id);
      onChange(await getAppointment(a.id));
      setRemoving(null);
      push("success", "Paiement supprimé");
    } catch {
      push("danger", "Suppression impossible");
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="card travail__block payments">
      <div className="travail__block-head">
        <h2 className="travail__block-title">Montants</h2>
        <span className="travail__block-actions">
          <Button
            variant="ghost"
            size="sm"
            disabled={locked}
            onClick={() => void openDocument("quote")}
          >
            {a.quoteNumber ? `Devis ${a.quoteNumber}` : "Devis"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={locked}
            onClick={() => void openDocument("invoice")}
          >
            {a.invoiceNumber ? `Facture ${a.invoiceNumber}` : "Facture"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={locked || sending}
            title="Envoyer la facture (ou le devis s'il n'y a pas de facture) par courriel au client"
            onClick={() => void emailDocument(a.invoiceId || !a.quoteNumber ? "invoice" : "quote")}
          >
            Envoyer par courriel
          </Button>
          <Button variant="primary" size="sm" disabled={locked} onClick={() => setCollecting(true)}>
            Encaisser
          </Button>
        </span>
      </div>

      {a.invoiceId && a.invoiceStatus ? (
        <div className="payments__invoice">
          <span className="payments__invoice-facts">
            <InvoiceStatusBadge status={a.invoiceStatus} />
            {a.invoiceDueAt && a.invoiceStatus !== "payee" ? (
              <span>Échéance le {formatDateTime(a.invoiceDueAt, { dateStyle: "medium" })}</span>
            ) : null}
            {a.billedClientName ? <span>Facturée à {a.billedClientName}</span> : null}
          </span>
        </div>
      ) : billables.length > 0 || a.billedClientId ? (
        <label className="payments__billing">
          <span className="payments__billing-label">Facturer à</span>
          <Select
            className="payments__billing-select"
            value={a.billedClientId ?? ""}
            disabled={locked}
            onChange={(e) => void setBilledClient(e.target.value || null)}
          >
            <option value="">{a.clientName} (client du véhicule)</option>
            {billables.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {CLIENT_TYPE_LABELS[c.type]}
              </option>
            ))}
            {a.billedClientId && !billables.some((c) => c.id === a.billedClientId) ? (
              <option value={a.billedClientId}>{a.billedClientName ?? "Autre client"}</option>
            ) : null}
          </Select>
        </label>
      ) : null}

      {sending ? (
        <EmailSendForm
          defaultTo={defaultTo}
          sending={working}
          what="la facture"
          onSend={(to, message) => void send(to, message)}
          onCancel={() => setSending(false)}
        />
      ) : null}

      <dl className="payments__totals">
        {amountLines(a).map((line) => (
          <div
            key={line.key}
            className={[
              "payments__line",
              line.strong && "payments__line--strong",
              line.muted && "payments__line--muted",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <dt>{line.label}</dt>
            <dd>{formatCad(line.cents)}</dd>
          </div>
        ))}
        <div className={`payments__due${settled ? " payments__due--settled" : ""}`}>
          <dt>{settled ? "Payée" : "Dû"}</dt>
          <dd>{formatCad(a.dueCents)}</dd>
        </div>
      </dl>

      {a.payments.length === 0 ? (
        <p className="travail__empty">Aucun paiement enregistré.</p>
      ) : (
        <table className="table payments__table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Méthode</th>
              <th>Référence</th>
              <th className="travail__num">Montant</th>
              {isAdmin ? <th aria-label="Actions" /> : null}
            </tr>
          </thead>
          <tbody>
            {a.payments.map((p) => (
              <tr
                key={p.id}
                className={p.kind === "remboursement" ? "payments__refund" : undefined}
              >
                <td className="payments__date">
                  {formatDateTime(p.paidAt, { dateStyle: "medium" })}
                </td>
                <td>{PAYMENT_KIND_LABELS[p.kind]}</td>
                <td>{PAYMENT_METHOD_LABELS[p.method]}</td>
                <td className="payments__ref">{p.reference ?? "—"}</td>
                <td className="travail__num">
                  {p.kind === "remboursement" ? "−" : ""}
                  {formatCad(p.amountCents)}
                </td>
                {isAdmin ? (
                  <td className="payments__actions">
                    <Button
                      variant="ghost-danger"
                      size="sm"
                      disabled={locked}
                      onClick={() => setRemoving(p)}
                    >
                      Supprimer
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <PaymentModal
        open={collecting}
        appointment={a}
        onClose={() => setCollecting(false)}
        onSaved={(view) => {
          setCollecting(false);
          onChange(view);
        }}
      />

      <Modal
        open={removing !== null}
        title="Supprimer ce paiement ?"
        onClose={() => setRemoving(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)} disabled={working}>
              Annuler
            </Button>
            <Button variant="danger" disabled={working} onClick={() => void remove()}>
              Supprimer
            </Button>
          </>
        }
      >
        {removing ? (
          <p>
            {PAYMENT_KIND_LABELS[removing.kind]} de {formatCad(removing.amountCents)} (
            {PAYMENT_METHOD_LABELS[removing.method]}) du{" "}
            {formatDateTime(removing.paidAt, { dateStyle: "long" })}. Le payé et le dû seront
            recalculés.
          </p>
        ) : null}
      </Modal>
    </section>
  );
}
